import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Button, Checkbox, Chip, ClickAwayListener, Divider, FormControl,
  ListItemText, MenuItem, MenuList, Paper, Popper, Select,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import BookmarkBorderIcon  from '@mui/icons-material/BookmarkBorder'
import ViewSidebarIcon     from '@mui/icons-material/ViewSidebar'
import CompareArrowsIcon  from '@mui/icons-material/CompareArrows'
import { DatePicker }     from '@mui/x-date-pickers'
import moment             from 'moment'
import { loadFilterOptions } from '../../reducers/activityReducer'
import {
  setAggLevel, setDateFrom, setDateTo,
  setOperator, setVendor, setLayer, setSiteId,
  setServer, setTac, setTech2, setNumCharts,
} from '../../reducers/filtersReducer'
import { applyAndFetchKpiData, fetchTech2Data }    from '../../actions/kpiDataActions'
import { clearTechData }                            from '../../reducers/kpiDataReducer'
import ActivityKPIDashboardExportButton             from '../charts/ActivityKPIDashboardExportButton'

const AGG_LEVELS  = ['Activity', 'Operator', 'Server', 'TAC', 'Vendor', 'SiteId', 'Layer']
const TECHS       = ['5G', '4G', '3G', '2G']
const TECH_COLORS = {
  '5G': { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' },
  '4G': { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
  '3G': { bg: '#fff3e0', color: '#bf360c', border: '#ffcc80' },
  '2G': { bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
}

function Label({ children }) {
  return (
    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '3px' }}>
      {children}
    </Typography>
  )
}

function FilterSelect({ label, value = [], options = [], onChange }) {
  const selected   = Array.isArray(value) ? value : (value ? [value] : [])
  const allSelected = options.length > 0 && selected.length === options.length

  const handleToggleAll = (e) => {
    e.stopPropagation()
    onChange(allSelected ? [] : [...options])
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Label>{label}</Label>
      <FormControl size="small">
        <Select
          multiple
          displayEmpty
          value={selected}
          onChange={e => onChange(e.target.value)}
          renderValue={sel => sel.length === 0
            ? <span style={{ color: '#aaa', fontSize: 11 }}>(All)</span>
            : <span style={{ fontSize: 11 }}>{sel.length === 1 ? sel[0] : `${sel.length} selected`}</span>
          }
          sx={{
            height: 28, fontSize: 11, minWidth: 110,
            bgcolor: selected.length > 0 ? 'primary.light' : 'background.default',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: selected.length > 0 ? 'primary.main' : 'divider'
            },
          }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 280, minWidth: 180 } } }}
        >
          {/* Select All */}
          <MenuItem onMouseDown={handleToggleAll} sx={{ fontSize: 11, py: '2px', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Checkbox
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              size="small" sx={{ py: 0, pr: 0.5 }}
            />
            <ListItemText
              primary={allSelected ? 'Deselect All' : 'Select All'}
              primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}
            />
          </MenuItem>
          {options.map(o => (
            <MenuItem key={o} value={o} sx={{ fontSize: 11, py: '2px' }}>
              <Checkbox checked={selected.includes(o)} size="small" sx={{ py: 0, pr: 0.5 }} />
              <ListItemText primary={o} primaryTypographyProps={{ fontSize: 11 }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export default function ActivityKPIDashboardFilterBar({ onOpenPicker, showMarks, onToggleMarks, showLegend, onToggleLegend, gridRef, activityTitle }) {
  const dispatch   = useDispatch()
  const selected   = useSelector(s => s.activity.selected)
  const opts       = useSelector(s => s.activity.filterOptions)
  const filters    = useSelector(s => s.filters)
  const loading    = useSelector(s => s.kpiData.loading)
  const compareRef = useRef(null)
  const [techPopover, setTechPopover] = useState(false)

  const { tech1, tech2, numCharts } = filters
  const comparing      = tech2 !== null
  const c2             = tech2 ? TECH_COLORS[tech2] : null
  const availableTechs = TECHS.filter(t => t !== tech1)

  const toCSV = (v) => {
    if (!v) return null
    const arr = Array.isArray(v) ? v : [v]
    return arr.length > 0 ? arr.join(',') : null
  }

  const refreshCascade = (patch) => {
    const merged = { ...filters, ...patch }
    dispatch(loadFilterOptions({
      id: selected.activityId,
      filters: {
        operator: toCSV(merged.operator),
        vendor:   toCSV(merged.vendor),
        layer:    toCSV(merged.layer),
        siteId:   toCSV(merged.siteId),
        server:   toCSV(merged.server),
        tac:      toCSV(merged.tac),
      },
    }))
  }

  const handleToggleCompare = () => {
    if (comparing) {
      // Already comparing — open dropdown to change tech or show X to remove
      setTechPopover(v => !v)
    } else {
      setTechPopover(v => !v)
    }
  }

  const handleRemoveCompare = () => {
    dispatch(clearTechData(tech2))
    dispatch(setTech2(null))
    dispatch(applyAndFetchKpiData())
    setTechPopover(false)
  }

  const handleSelectTech2 = (tech) => {
    setTechPopover(false)
    if (tech2 && tech2 !== tech) dispatch(clearTechData(tech2))  // clear old tech2
    dispatch(setTech2(tech))
    dispatch(fetchTech2Data(tech))
  }

  const btnSx = (active) => ({
    height: 28, px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
    border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
    borderRadius: '6px !important', gap: 0.5,
    '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.main' },
  })

  return (
    <Paper elevation={0} square sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>

      {/* ══ ROW 1: Activity | Dates + Apply | Options ═════════════════════ */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 2.5, pt: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>

        {/* Activity */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Label>Activity</Label>
          <Chip
            label={selected?.title ?? 'None selected'}
            onClick={onOpenPicker}
            deleteIcon={<Typography sx={{ fontSize: 10, mr: 0.5 }}>✎</Typography>}
            onDelete={onOpenPicker}
            size="small"
            sx={{
              height: 28, borderRadius: 20, bgcolor: 'primary.light',
              border: '1px solid', borderColor: 'primary.main',
              color: 'primary.dark', fontWeight: 500, fontSize: 11,
              maxWidth: 300, cursor: 'pointer',
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Dates + Apply */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Label>First Day</Label>
          <DatePicker
            format="YYYY-MM-DD"
            value={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD') : null}
            onChange={v => dispatch(setDateFrom(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
            maxDate={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD').subtract(1, 'day') : undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Label>Last Day</Label>
          <DatePicker
            format="YYYY-MM-DD"
            value={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD') : null}
            onChange={v => dispatch(setDateTo(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
            minDate={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD').add(1, 'day') : undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
          />
        </Box>
        <Button variant="contained" size="small" disabled={!selected || loading}
          onClick={() => dispatch(applyAndFetchKpiData())}
          sx={{ height: 28, alignSelf: 'flex-end', px: 2.5 }}>
          Apply
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* View */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Label>View</Label>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>

            <ToggleButton value="marks" selected={showMarks} onChange={onToggleMarks} size="small" sx={btnSx(showMarks)}>
              <BookmarkBorderIcon sx={{ fontSize: 13 }} />
              Marks
            </ToggleButton>

            <ToggleButton value="legend" selected={showLegend} onChange={onToggleLegend} size="small" sx={btnSx(showLegend)}>
              <ViewSidebarIcon sx={{ fontSize: 13 }} />
              Legend
            </ToggleButton>

            <Button ref={compareRef} onClick={handleToggleCompare} size="small"
              variant={comparing ? 'contained' : 'outlined'}
              sx={{
                height: 28, px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                gap: 0.5, minWidth: 0,
                ...(comparing ? {
                  bgcolor: 'primary.main', color: '#fff',
                  '&:hover': { bgcolor: 'primary.dark' },
                } : {
                  borderColor: 'divider', color: 'text.secondary',
                  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                })
              }}>
              <CompareArrowsIcon sx={{ fontSize: 13 }} />
              {comparing && tech2 ? tech2 : 'Compare'}
            </Button>

            {/* Tech picker popup */}
            <Popper open={techPopover} anchorEl={compareRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
              <ClickAwayListener onClickAway={() => setTechPopover(false)}>
                <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 130 }}>
                  <Box sx={{ px: 1.5, py: '6px', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Compare with
                    </Typography>
                  </Box>
                  <MenuList dense>
                    {availableTechs.map(t => {
                      const c = TECH_COLORS[t]
                      return (
                        <MenuItem key={t} onClick={() => handleSelectTech2(t)} selected={tech2 === t}
                          sx={{ gap: 1, '&.Mui-selected': { bgcolor: 'primary.light' } }}>
                          <Chip label={t} size="small"
                            sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 10, height: 20, borderRadius: 1 }} />
                        </MenuItem>
                      )
                    })}
                    {comparing && (
                      <MenuItem onClick={handleRemoveCompare}
                        sx={{ gap: 1, borderTop: '1px solid', borderColor: 'divider', color: 'error.main' }}>
                        <Typography sx={{ fontSize: 11 }}>✕ Remove Compare</Typography>
                      </MenuItem>
                    )}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Popper>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 20, alignSelf: 'center' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 500 }}>Charts</Typography>
              <FormControl size="small">
                <Select value={numCharts} onChange={e => dispatch(setNumCharts(Number(e.target.value)))}
                  sx={{ height: 28, fontSize: 11, minWidth: 52, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
                  {[1, 2, 3].map(n => <MenuItem key={n} value={n} sx={{ fontSize: 11 }}>{n}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Export */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Label>Export</Label>
          <ActivityKPIDashboardExportButton containerRef={gridRef} title={activityTitle ?? 'Activity_KPI_Dashboard'} />
        </Box>
      </Box>

      {/* ══ ROW 2: Filters + Apply | Aggregation + Apply ══════════════════ */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, px: 2.5, pt: 0.75, pb: 1, flexWrap: 'wrap' }}>

        {/* Cascade filters */}
        <FilterSelect label="Operator" value={filters.operator} options={opts?.operators ?? []}
          onChange={v => { dispatch(setOperator(v)); refreshCascade({ operator: v }) }} />
        <FilterSelect label="Server"   value={filters.server}   options={opts?.servers   ?? []}
          onChange={v => { dispatch(setServer(v));   refreshCascade({ server: v })   }} />
        <FilterSelect label="TAC/LAC"  value={filters.tac}      options={opts?.tacs      ?? []}
          onChange={v => { dispatch(setTac(v));      refreshCascade({ tac: v })      }} />
        <FilterSelect label="Vendor"   value={filters.vendor}   options={opts?.vendors   ?? []}
          onChange={v => { dispatch(setVendor(v));   refreshCascade({ vendor: v })   }} />
        <FilterSelect label="Layer"    value={filters.layer}    options={opts?.layers    ?? []}
          onChange={v => { dispatch(setLayer(v));    refreshCascade({ layer: v })    }} />
        <FilterSelect label="Site ID"  value={filters.siteId}   options={opts?.siteIds   ?? []}
          onChange={v => { dispatch(setSiteId(v));   refreshCascade({ siteId: v })   }} />

        <Button variant="contained" size="small" disabled={!selected || loading}
          onClick={() => { refreshCascade({}); dispatch(applyAndFetchKpiData()) }}
          sx={{ height: 28, alignSelf: 'flex-end', px: 2.5 }}>
          Apply
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Aggregation */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Label>Aggregation</Label>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ToggleButtonGroup value={filters.aggLevel} exclusive size="small"
              onChange={(_, v) => { if (v) dispatch(setAggLevel(v)) }} sx={{ height: 28 }}>
              {AGG_LEVELS.map(l => (
                <ToggleButton key={l} value={l} sx={{
                  px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                  '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                }}>{l}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Button variant="contained" size="small" disabled={!selected || loading}
              onClick={() => dispatch(applyAndFetchKpiData())}
              sx={{ height: 28, px: 2.5 }}>
              Apply
            </Button>
          </Box>
        </Box>

      </Box>
    </Paper>
  )
}